import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoginPayload } from '@edtech-ui/core/models/interfaces/login';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { SharedModule } from '@edtech-ui/shared/shared.module';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { normalizeTimeZone } from '@edtech-ui/core/utils/date-time.util';
import { FireBaseServiceService } from '@edtech-ui/core/services/firebaseService/fire-base-service.service';
import { OneSignalService } from '@edtech-ui/core/services/one-signal-service/one-signal.service';
import { emailOrPhoneValidator } from '@edtech-ui/core/utils/identifier-validator';
import { passwordStrengthValidator } from '@edtech-ui/core/utils/password-validators';
import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';

@Component({
	selector: 'app-user-registration-login-with-phone',
	standalone: true,
	imports: [SharedModule, CommonModule, ReactiveFormsModule, RouterModule, MatProgressSpinnerModule],
	templateUrl: './user-registration-login-with-phone.component.html',
	styleUrl: './user-registration-login-with-phone.component.scss',
})
export class UserRegistrationLoginWithPhoneComponent implements OnInit, OnDestroy {

	loginMethod: 'password' | 'otp' = 'password';
	showPassword = false;
	passwordForm!: FormGroup;
	otpForm!: FormGroup;
	subscription = new Subscription();
	isLoading: boolean = false;
	redirectUrl: string = '';
	forgotPasswordStep: number = 0;
	forgotPasswordAccountForm!: FormGroup;
	forgotPasswordVerifyForm!: FormGroup;
	forgotPasswordResetForm!: FormGroup;

	forgotPasswordEmailOrPhone: string = '';
	forgotPasswordOtpValues: string[] = new Array(6).fill('');
	forgotPasswordResetToken: string = '';
	showFpPassword = false;
	showFpConfirmPassword = false;

	isResendingOtp: boolean = false;
	resendCountdown: number = 0;
	resendInterval: any;
	get fpHasMinLength(): boolean { return this.forgotPasswordResetForm?.get('newPassword')?.value?.length >= 8; }
	get fpHasUppercase(): boolean { return /[A-Z]/.test(this.forgotPasswordResetForm?.get('newPassword')?.value || ''); }
	get fpHasLowercase(): boolean { return /[a-z]/.test(this.forgotPasswordResetForm?.get('newPassword')?.value || ''); }
	get fpHasNumber(): boolean { return /[0-9]/.test(this.forgotPasswordResetForm?.get('newPassword')?.value || ''); }

	constructor(
		private router: Router,
		private _toastr: ToastrService,
		private _authService: AuthServiceService,
		private route: ActivatedRoute,
		private _fireBaseService: FireBaseServiceService,
		private _oneSignalService: OneSignalService,
		private _authRedirectService: AuthRedirectService
	) {
		this.passwordForm = new FormGroup({
			identifier: new FormControl('', [Validators.required, emailOrPhoneValidator]),
			password: new FormControl('', [Validators.required, Validators.minLength(8)])
		});

		this.otpForm = new FormGroup({
			identifier: new FormControl('', [Validators.required, emailOrPhoneValidator])
		});

		this.forgotPasswordAccountForm = new FormGroup({
			identifier: new FormControl('', [Validators.required, Validators.email])
		});

		this.forgotPasswordResetForm = new FormGroup(
			{
				newPassword: new FormControl('', [
					Validators.required,
					Validators.minLength(8),
					passwordStrengthValidator
				]),
				confirmPassword: new FormControl('', Validators.required),
			},
			{ validators: this.passwordMatchValidator }
		);
	}

	ngOnInit(): void {
		this.route.queryParams.subscribe(params => {
			this.redirectUrl = params['redirectUrl'];
			if (params['phone']) {
				this.otpForm.patchValue({ identifier: params['phone'] });
				this.passwordForm.patchValue({ identifier: params['phone'] });
			}
			if (params['email']) {
				this.otpForm.patchValue({ identifier: params['email'] });
				this.passwordForm.patchValue({ identifier: params['email'] });
			}
		});
		this.redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl') || this.redirectUrl;
		this._authRedirectService.redirectIfLoggedIn(this.redirectUrl);
	}

	onIdentifierInput(event: Event, form: FormGroup, controlName: string): void {
		const input = event.target as HTMLInputElement;
		let rawValue = input.value;

		const control = form.get(controlName);
		if (!control) return;
		control.markAsTouched();

		if (/^\d+$/.test(rawValue) && rawValue.length > 10) {
			rawValue = rawValue.slice(0, 10);
			input.value = rawValue;
		}

		control.setValue(rawValue, { emitEvent: false });
	}

	private withTimeout<T>(promise: Promise<T>, ms = 3000): Promise<T | ''> {
		const timeout = new Promise<''>((resolve) => setTimeout(() => { resolve('') }, ms));
		return Promise.race([promise, timeout]);
	}

	async onPasswordLogin() {
		if (this.passwordForm.invalid) {
			this.passwordForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
		const language = navigator.language || '';
		const country = language.includes('-') ? language.split('-')[1] : 'Unknown';
		let fcmToken = localStorage.getItem('fcmToken') || '';
		let playerId = localStorage.getItem('onesignal_player_id') || '';

		if (!fcmToken || !playerId) {
			const [fetchedFcm, fetchedPlayer] = await Promise.all([
				!fcmToken ? this.withTimeout(this._fireBaseService.getFCMToken(), 5000) : Promise.resolve(fcmToken),
				!playerId ? this.withTimeout(this._oneSignalService.getPlayerId(), 5000) : Promise.resolve(playerId),
			]);
			fcmToken = fcmToken || fetchedFcm || '';
			playerId = playerId || fetchedPlayer || '';
		}

		const identifier = this.passwordForm.value.identifier;
		const isPhone = /^[6-9]\d{9}$/.test(identifier);

		const loginPayload: LoginPayload = {
			email: isPhone ? '' : identifier,
			phone: isPhone ? identifier : '',
			password: this.passwordForm.value.password,
			fcm_token: fcmToken || '',
			onesignal_player_id: playerId || '',
			is_default: true,
			login_source: "web",
			user_timezone: normalizeTimeZone(timeZone),
			country: country
		};

		this.subscription.add(
			this._authService.postLogin(loginPayload).subscribe({
				next: (response) => {
					this.isLoading = false;

					if (!response.Success) {
						this._toastr.error(response.Message);
						return;
					}

					if (response.Data?.user_id) {
						const newUserId = response.Data.user_id.toString();
						localStorage.setItem('user_id', newUserId);
						this._authService.currentUserId.next(newUserId);
					}

					if (response.Data?.phone_email) {
						this._authService.currentUserEmail.next(response.Data.phone_email);
						localStorage.setItem('email', response.Data.phone_email);
					}

					if (response.Success && response.Data?.refresh_token && response.Data?.token) {
						if (response.Data?.role_id) {
							this._authService.setRoleId(response.Data.role_id.toString());
							localStorage.setItem('role_id', response.Data.role_id.toString());
						}
						this._authService.setToken(response.Data.token);
						localStorage.setItem('refresh_token', response.Data.refresh_token);
						this._toastr.success(response.Message);

						if (response.Data?.modules) {
							this._authService.setModules(response.Data.modules);
						}

						
						const pendingRoundId = localStorage.getItem('pending_practice_round_id');
						if (pendingRoundId && (response.Data.role_id === 2 || response.Data.role_id === 1)) {
							localStorage.removeItem('pending_practice_round_id');
							this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
						} else {
							this._authRedirectService.handlePostLoginRedirect(this.redirectUrl);
						}
					} else {
						this._toastr.error(response.Message);
					}
				},
				error: () => {
					this.isLoading = false;
					this._toastr.error('Server error occurred. Please try again.');
				}
			})
		);
	}

	async onOtpLogin() {
		if (this.otpForm.invalid) {
			this.otpForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
		const language = navigator.language || '';
		const country = language.includes('-') ? language.split('-')[1] : 'Unknown';
		let fcmToken = localStorage.getItem('fcmToken') || '';
		let playerId = localStorage.getItem('onesignal_player_id') || '';

		if (!fcmToken || !playerId) {
			const [fetchedFcm, fetchedPlayer] = await Promise.all([
				!fcmToken ? this.withTimeout(this._fireBaseService.getFCMToken(), 5000) : Promise.resolve(fcmToken),
				!playerId ? this.withTimeout(this._oneSignalService.getPlayerId(), 5000) : Promise.resolve(playerId),
			]);

			fcmToken = fcmToken || fetchedFcm || '';
			playerId = playerId || fetchedPlayer || '';
		}

		const identifier = this.otpForm.value.identifier;
		const isPhone = /^[6-9]\d{9}$/.test(identifier);

		const loginPayload: LoginPayload = {
			email: isPhone ? '' : identifier,
			phone: isPhone ? identifier : '',
			fcm_token: fcmToken || '',
			onesignal_player_id: playerId || '',
			is_default: true,
			login_source: "web",
			user_timezone: normalizeTimeZone(timeZone),
			country: country
		};

		this.subscription.add(
			this._authService.postLogin(loginPayload).subscribe({
				next: (response) => {
					this.isLoading = false;
					if (!response.Success && response.Status === 401) {
						this._toastr.info(response.Message);
						this.router.navigate(['/mobile-verify'], { queryParams: { redirectUrl: this.redirectUrl } });
						return;
					}

					if (response.Data?.user_id) {
						const newUserId = response.Data.user_id.toString();
						localStorage.setItem('user_id', newUserId);
						this._authService.currentUserId.next(newUserId);
					}

					if (response.Data?.phone_email) {
						this._authService.currentUserEmail.next(response.Data.phone_email);
						localStorage.setItem('email', response.Data.phone_email);
					}

					this._toastr.success(response.Message);
					if (isPhone) {
						this.router.navigate(['/login-phone-otp'], {
							queryParams: {
								phone: identifier,
								redirectUrl: this.redirectUrl
							}
						});
					} else {
						this.router.navigate(['/login-email'], {
							queryParams: {
								redirectUrl: this.redirectUrl
							}
						});
					}
				},
				error: () => {
					this.isLoading = false;
					this._toastr.error('Server error occurred. Please try again.');
				}
			})
		);
	}


	goToForgotPassword() {
		this.forgotPasswordStep = 1;
		this.forgotPasswordAccountForm.reset();
		this.forgotPasswordEmailOrPhone = '';
	}


	forgotPasswordBack() {
		if (this.forgotPasswordStep > 1 && this.forgotPasswordStep < 4) {
			this.forgotPasswordStep--;
		} else {
			this.forgotPasswordStep = 0;
		}
	}


	startResendTimer() {
		this.resendCountdown = 30;
		if (this.resendInterval) clearInterval(this.resendInterval);
		this.resendInterval = setInterval(() => {
			this.resendCountdown--;
			if (this.resendCountdown <= 0) {
				clearInterval(this.resendInterval);
			}
		}, 1000);
	}


	async submitForgotPasswordAccount() {
		if (this.forgotPasswordAccountForm.invalid) {
			this.forgotPasswordAccountForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;
		let fcmToken = localStorage.getItem('fcmToken') || '';
		if (!fcmToken) {
			fcmToken = await this.withTimeout(this._fireBaseService.getFCMToken(), 5000) as string || '';
		}

		const identifier = this.forgotPasswordAccountForm.value.identifier;
		const payload = {
			phone_email: identifier,
			fcm_token: fcmToken
		};

		this.subscription.add(
			this._authService.postForgotPassword(payload).subscribe({
				next: (response: any) => {
					this.isLoading = false;
					if (response.Success) {
						this._toastr.success(response.Message);
						this.forgotPasswordEmailOrPhone = response.Data?.Email || response.Data?.phone_email || identifier;
						this.forgotPasswordOtpValues.fill('');
						this.forgotPasswordStep = 2;
						this.startResendTimer();
					} else {
						this._toastr.error(response.Message);
					}
				},
				error: () => {
					this.isLoading = false;
					this._toastr.error('Something went wrong while sending OTP.');
				}
			})
		);
	}


	trackByIndex(index: number, obj: any): any {
		return index;
	}


	handleFpOtpInput(event: any, index: number) {
		const input = event.target as HTMLInputElement;
		const value = input.value;

		if (!/^\d$/.test(value)) {
			input.value = '';
			this.forgotPasswordOtpValues[index] = '';
			return;
		}

		this.forgotPasswordOtpValues[index] = value;

		if (value && index < 5) {
			const nextInput = input.nextElementSibling as HTMLInputElement;
			nextInput?.focus();
		}
	}


	handleFpKeyDown(event: KeyboardEvent, index: number) {
		const target = event.target as HTMLInputElement;
		if (event.key === 'Backspace' && !this.forgotPasswordOtpValues[index] && index > 0) {
			const prevInput = target.previousElementSibling as HTMLInputElement;
			prevInput?.focus();
		}
	}


	handleFpOtpPaste(event: ClipboardEvent) {
		event.preventDefault();
		const clipboardData = event.clipboardData || (window as any).clipboardData;
		const pastedText = clipboardData?.getData('text');
		if (!pastedText) return;

		const numbers = pastedText.replace(/\D/g, '').substring(0, 6);
		for (let i = 0; i < numbers.length; i++) {
			this.forgotPasswordOtpValues[i] = numbers[i];
		}

		setTimeout(() => {
			const inputs = document.querySelectorAll('.otp-inputs input') as NodeListOf<HTMLInputElement>;
			if (inputs && inputs.length > 0) {
				const focusIndex = Math.min(numbers.length, 5);
				inputs[focusIndex].focus();
			}
		}, 10);
	}


	isFpOtpFilled(): boolean {
		return this.forgotPasswordOtpValues.join('').length === 6;
	}


	getFpOtpCode(): string {
		return this.forgotPasswordOtpValues.join('');
	}


	resendFpOtp() {
		if (this.resendCountdown > 0) return;
		this.isResendingOtp = true;
		this.subscription.add(
			this._authService.postResendOtp(this.forgotPasswordEmailOrPhone).subscribe({
				next: (response: any) => {
					this.isResendingOtp = false;
					if (response?.Success) {
						this._toastr.success(response.Message || 'OTP sent successfully');
						this.startResendTimer();
					} else {
						this._toastr.error(response.Message || 'Failed to resend OTP');
					}
				},
				error: () => {
					this.isResendingOtp = false;
					this._toastr.error('Something went wrong while resending OTP');
				}
			})
		);
	}


	submitForgotPasswordVerify() {
		const otp = this.getFpOtpCode();
		if (otp.length === 6 && /^\d{6}$/.test(otp)) {
			this.isLoading = true;
			const data = {
				email: this.forgotPasswordEmailOrPhone,
				otp: Number(otp),
			};
			this.subscription.add(
				this._authService.postVerifyResetOtp(data).subscribe({
					next: (response: any) => {
						this.isLoading = false;
						if (response?.Success) {
							this._toastr.success('OTP Verified');
							this.forgotPasswordResetToken = response.Data.reset_token;
							this.forgotPasswordResetForm.reset();
							this.forgotPasswordStep = 3;
						} else {
							this._toastr.error(response.Message || 'Invalid OTP');
							this.forgotPasswordOtpValues.fill('');
							setTimeout(() => {
								const firstInput = document.getElementById('otp-input-0') as HTMLInputElement;
								if (firstInput) firstInput.focus();
							}, 0);
						}
					},
					error: () => {
						this.isLoading = false;
						this._toastr.error('Verification failed. Please try again.');
						this.forgotPasswordOtpValues.fill('');
						setTimeout(() => {
							const firstInput = document.getElementById('otp-input-0') as HTMLInputElement;
							if (firstInput) firstInput.focus();
						}, 0);
					}
				})
			);
		} else {
			this._toastr.error('Invalid OTP format');
		}
	}


	passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const password = control.get('newPassword')?.value;
		const confirmPassword = control.get('confirmPassword')?.value;
		return password === confirmPassword ? null : { passwordMismatch: true };
	};

	submitForgotPasswordReset() {
		if (this.forgotPasswordResetForm.invalid) {
			this.forgotPasswordResetForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;
		const payload = {
			new_password: this.forgotPasswordResetForm.value.newPassword,
			email: this.forgotPasswordEmailOrPhone,
			reset_token: this.forgotPasswordResetToken,
		};

		this.subscription.add(
			this._authService.postSetNewPassword(payload).subscribe({
				next: (response: any) => {
					this.isLoading = false;
					if (response.Success) {
						this._toastr.success(response.Message);
						this.forgotPasswordStep = 4;
					} else {
						this._toastr.error(response.Message);
					}
				},
				error: () => {
					this.isLoading = false;
					this._toastr.error('Something went wrong while resetting password.');
				}
			})
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		if (this.resendInterval) clearInterval(this.resendInterval);
	}
}
